import { Box, type BoxProps } from '@chakra-ui/react'
import { orderBy } from 'lodash-es'

import { useUser } from '@/client/context/user'
import isLiveHelpRequestActive from '@/client/services/liveHelp/isLiveHelpRequestActive'
import { hasUserRole } from '@/client/services/user'
import { getUserLevelInWorld, isLevelAtLeastMinLevel } from '@/client/services/worlds'

import type { PostDetails } from '@/components/Community/Post/types'
import useRealtimeLiveHelpRequests from '@/components/Worlds/hooks/post/useRealtimeLiveHelpRequests'

import AuthorLiveHelpBanner from './internal/AuthorLiveHelpBanner'
import NonTutorLiveHelpBanner from './internal/NonTutorLiveHelpBanner'
import TutorLiveHelpBanner from './internal/TutorLiveHelpBanner'

export default function LiveHelpBanner({
  post,
  postIsFullView,
  ...props
}: { post: PostDetails; postIsFullView: boolean } & BoxProps): JSX.Element | null {
  const currentUser = useUser()
  const { liveHelpRequests, loading } = useRealtimeLiveHelpRequests(post.id)

  const mostRecentLiveHelpRequest = orderBy(
    liveHelpRequests,
    request => -request.createdAt.toMillis()
  )[0]
  const currentUserLastHelpedLearnerAt =
    liveHelpRequests.find(request => request.session?.host.id === currentUser.id)?.createdAt ?? null

  const { user, channel } = post
  const { liveHelpInfo } = post
  if (liveHelpInfo == null) {
    return null
  }
  const { worldDetails, postStatus } = liveHelpInfo

  if (worldDetails == null || postStatus == null || channel?.type !== 'question') {
    return null
  }

  const userLevelInWorld = getUserLevelInWorld({
    user: currentUser,
    worldSlug: worldDetails.slug,
  })

  const isAuthor = currentUser.id === user.id
  const canGiveLiveHelp =
    !isAuthor &&
    isLevelAtLeastMinLevel({ level: userLevelInWorld, minLevel: 'tutor' }) &&
    hasUserRole(currentUser, 'user:tutor')

  if (loading) {
    return null
  }

  if (postStatus === 'closed') {
    return null
  }

  if (isAuthor) {
    if (postStatus === 'solved') {
      return null
    }

    return (
      <Box {...props}>
        <AuthorLiveHelpBanner
          post={post}
          liveHelpRequest={mostRecentLiveHelpRequest}
          postIsFullView={postIsFullView}
        />
      </Box>
    )
  }

  if (canGiveLiveHelp) {
    if (mostRecentLiveHelpRequest == null || !isLiveHelpRequestActive(mostRecentLiveHelpRequest)) {
      return null
    }

    if (postStatus === 'solved' && currentUserLastHelpedLearnerAt == null) {
      return null
    }

    return (
      <Box {...props}>
        <TutorLiveHelpBanner
          post={post}
          liveHelpRequest={mostRecentLiveHelpRequest}
          currentUserLastHelpedLearnerAt={currentUserLastHelpedLearnerAt}
          postIsFullView={postIsFullView}
        />
      </Box>
    )
  }

  if (mostRecentLiveHelpRequest == null || postStatus === 'solved') {
    return null
  }

  return (
    <Box {...props}>
      <NonTutorLiveHelpBanner post={post} liveHelpRequest={mostRecentLiveHelpRequest} />
    </Box>
  )
}
